import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const SbiGoldLoanEmiCalcu = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
      question: "Is this calculator specific to SBI gold loans?",
      answer:
        "Yes—it’s tailored to SBI’s gold loan terms—but you may adjust rate and tenure to simulate other lenders in a rough comparison.",
    },
    {
      question: "How does foreclosure affect EMI?",
      answer:
        "If you repay the gold loan early, interest is calculated only till closure—saving you money on interest and eliminating future EMI.",
    },
    {
      question: "Can I prepay in parts?",
      answer:
        "Yes, SBI allows partial or full prepayments subject to terms. Doing so reduces your outstanding loan amount and total interest.",
    },
    {
      question: "What interest rate does SBI offer?",
      answer:
        "Interest rates typically range from 8% to 10% per annum as per RBI, loan amount, gold quality, and borrower category.",
    },
    {
      question: "Will gold price changes affect my EMI?",
      answer:
        "No—EMI is based on disbursement. Gold price fluctuations affect LTV for future loans, but not current EMI.",
    },
    {
      question: "Do I need to pay processing fees?",
      answer:
        "Yes, there may be small processing charges depending on tenure and amount. These don’t affect EMI but add upfront cost.",
    },
    {
      question: "Can I get gold back before repayment ends?",
      answer:
        "Yes, SBI releases pledged gold upon receipt of full repayment or partial release if corresponding EMI is paid.",
    },
    {
      question: "What documents are required for a gold loan?",
      answer:
        "Minimal documents are needed: ID proof, address proof, and standard jewellry valuation & KYC—making it quick to process.",
    },
    {
      question: "Does the calculator include gold valuation margins?",
      answer:
        "No. EMI is calculated on actual loan amount. Jewellery valuation and market margin are separate steps during disbursement.",
    },
    {
      question: "How long does it take to process a gold loan?",
      answer:
        "Typically, SBI disburses the loan within a few hours of valuation and document submission due to minimal paperwork.",
    },
    {
      question: "Can NRI customers use this tool?",
      answer:
        "Yes. NRIs with KYC-compliant Indian bank accounts and documents can use this tool, though gold pledging rules may vary.",
    },
    {
      question: "Does gold purity affect EMI?",
      answer:
        "Indirectly—higher purity translates to higher loan amount for the same gold, which affects EMI size.",
    },
    {
      question: "Can I download the amortisation schedule?",
      answer:
        "Yes. The tool allows you to export the full amortisation schedule to review or maintain financial records.",
    },
    {
      question: "Are there penalties for late EMI payment?",
      answer:
        "Yes—delayed payments attract a higher rate of interest on the overdue amount. Be sure to pay on time to avoid penalties.",
    },
    {
      question: "Is the calculator free and private?",
      answer:
        "Absolutely. It’s free, accessible 24/7, doesn’t require login, and keeps your inputs private—perfect for transparent financial planning.",
    },
  ];
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>SBI Gold Loan EMI Calculator - Easy & Accurate Estimation</title>
          <meta  name='description' content='Use our SBI gold loan EMI Calculator to calculate your monthly installments easily. Make informed decisions with precise EMI estimates.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/sbi-gold-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">SBI Gold Loan EMI Calculator</span>
          </li>
        </ol>
      </nav> */}
           <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
            SBI Gold Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
          <div className="w-full md:w-2/3 p-4 mx-auto">
            <div className="container mx-auto py-4">
              <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                Plan Your SBI Gold Loan Repayments with TheFinMaster’s EMI
                Calculator
              </h2>
              <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
                A gold loan is a quick and straightforward way to access funds
                during emergencies—be it medical needs, business expenses, or
                personal reasons. State Bank of India provides attractive gold
                loan schemes with competitive interest rates, minimal paperwork,
                and fast disbursal. But before pledging your jewellery, it's
                crucial to understand how much you'll repay each month.
                TheFinMaster’s SBI Gold Loan EMI Calculator empowers you to
                simulate your EMI, compare scenarios, and plan your repayment
                effectively.
              </p>
              </div>
              <div className="container mx-auto py-4">
                <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                  What is a Gold Loan EMI?
                </h2>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  A Gold Loan EMI (Equated Monthly Installment) is the EMI that
                  fixed monthly payment you pay to the SBI to repay your loan,
                  which is secured by against your principal pledged. gold. Each
                  EMI consists of the: Principal: The amount actually borrowed
                  against your gold Interest: Charged on the outstanding balance
                  loan amount In the initial months, the EMI is predominantly
                  more interest-heavy. InOver time, the share of the principal
                  increases repayment increases. Understanding this structure
                  helps you predict to manage your cash flow and manage your
                  finances more reliably.
                </p>
              </div>
              <div className="container mx-auto">
             
                <h2 className="text-xl md:text-2xl  bg-blue-100 font-medium  border-blue-600 pl-4 border-l-4 py-2">
                  What is the SBI Gold Loan EMI Calculator??
                </h2>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  The SBI Gold Loan EMI on Calculator TheFinMaster is a fast and
                  intuitive online tool that helps you estimate how much you'll
                  need to repay each month based on:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Loan Amount: Money borrowed against gold value</li>
                    <li>Annual Interest Rate</li>
                    <li>Loan Tenure (in months)</li>
                  </ul>
                  Once you enter these, the tool instantly displays:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Your exact monthly EMI</li>
                    <li>TotalAmount</li>{" "}
                    <li>
                      Total interest amount repayable (principal + interest)
                    </li>
                    <li>A full schedule</li>
                  </ul>
                  This enables you to explore different loan amounts, rates, or
                  durations, or and decide which suits you best
                </p>
              </div>
              <div className="container mx-auto py-4">
                <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-4">
                  Why Use TheFinMaster’s SBI Gold EMI Loan Calculator?
                </h2>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-xl lg:text-xl xl:text-lg">
                  Using our calculator gives you multiple advantages:
                  <ul className="list-ul disc-pl-6 mt-2">
                    <li>
                      <strong>Instant Results:</strong> Compute EMI swiftly
                    </li>
                    <li>
                      {" "}
                      <strong>No Sign-Up Needed:</strong> It's secure, secure,
                      and anonymous, and free
                    </li>
                    <li>
                      <strong>Scenario Planning:</strong> Instantly tweak inputs
                      to compare repayment options
                    </li>
                    <li>
                      <strong>Cost Clarity:</strong> Understand how much
                      interest you’re paying over time
                    </li>
                    <li>
                      <strong>Visual Amortisation:</strong> Chart: Monitor how
                      your
                    </li>
                    <li>
                      <strong>Mobile-Optimised:</strong> Works smoothly on
                      desktop and mobile devices
                    </li>
                  </ul>
                  With these tools, you can borrow with clarity and repay with
                  confidence, free from uncertainty.
                </p>
              </div>
              <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
              <div className="container mx-auto py-4">
                <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                  How Does the SBI Gold Loan EMI Calculator Work?
                </h2>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  The standard EMI formula used is:
                </p>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  EMI = [P × R × (1 + R)^N] / [(1 + R)^N – 1]
                </p>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  Where:
                </p>
                <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  <li>P = Principal (loan amount)</li>
                  <li>R = Monthly interest rate (Annual Rate ÷ 12 ÷ 100)</li>
                  <li>N = Loan tenure in months</li>
                </ul>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  <strong>Example Scenario:</strong>
                  <br />
                  Assume SBI gold loan of ₹2,00,000 at 9% annual interest,
                  repayable over 12 months:
                  <br />
                  P = ₹2,00,000
                  <br />
                  R = 0.75% (9 ÷ 12 ÷ 100)
                  <br />
                  N = 12 months
                  <br />
                  This yields:
                  <br />
                  EMI ≈ ₹17,484
                  <br />
                  Total Repayment ≈ ₹2,09,808
                  <br />
                  Total Interest ≈ ₹9,808
                  <br />
                  This helps you calculate exactly how much you’ll pay monthly
                  and overall.
                </p>
              </div>
              <div className="container mx-auto py-4">
                <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                  Sample Amortisation Schedule (First 6 Months)
                </h2>
                <div className="mt-6">
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border px-4 py-2 text-left">Month</th>
                          <th className="border px-4 py-2 text-left">
                            EMI (₹)
                          </th>
                          <th className="border px-4 py-2 text-left">
                            Interest (₹)
                          </th>
                          <th className="border px-4 py-2 text-left">
                            Principal (₹)
                          </th>
                          <th className="border px-4 py-2 text-left">
                            Outstanding Balance (₹)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["1", "17,484", "1,500", "15,984", "1,84,016"],
                          ["2", "17,484", "1,380", "16,104", "1,67,912"],
                          ["3", "17,484", "1,259", "16,225", "1,51,687"],
                          ["4", "17,484", "1,138", "16,346", "1,35,341"],
                          ["5", "17,484", "1,015", "16,469", "1,18,872"],
                          ["6", "17,484", "891", "16,593", "1,02,279"],
                        ].map(
                          (
                            [month, emi, interest, principal, balance],
                            index
                          ) => (
                            <tr key={index}>
                              <td className="border px-4 py-2">{month}</td>
                              <td className="border px-4 py-2">{emi}</td>
                              <td className="border px-4 py-2">{interest}</td>
                              <td className="border px-4 py-2">{principal}</td>
                              <td className="border px-4 py-2">{balance}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="md:hidden space-y-4 mt-4">
                    {[
                      ["1", "17,484", "1,500", "15,984", "1,84,016"],
                      ["2", "17,484", "1,380", "16,104", "1,67,912"],
                      ["3", "17,484", "1,259", "16,225", "1,51,687"],
                      ["4", "17,484", "1,138", "16,346", "1,35,341"],
                      ["5", "17,484", "1,015", "16,469", "1,18,872"],
                      ["6", "17,484", "891", "16,593", "1,02,279"],
                    ].map(
                      ([month, emi, interest, principal, balance], index) => (
                        <div
                          key={index}
                          className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border"
                        >
                          <div>
                            <span className="font-semibold">Month:</span>{" "}
                            {month}
                          </div>
                          <div>
                            <span className="font-semibold">EMI (₹):</span>{" "}
                            {emi}
                          </div>
                          <div>
                            <span className="font-semibold">Interest (₹):</span>{" "}
                            {interest}
                          </div>
                          <div>
                            <span className="font-semibold">
                              Principal (₹):
                            </span>{" "}
                            {principal}
                          </div>
                          <div>
                            <span className="font-semibold">
                              Outstanding Balance (₹):
                            </span>{" "}
                            {balance}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  You can extend this schedule for the full tenure—downloadable
                  for complete clarity.
                </p>
              </div>
              <div className="container mx-auto py-4">
                <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                  Factors That Affect Your Gold Loan EMI
                </h2>
                <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  <li>
                    <strong>Loan Amount:</strong> Higher amount results in
                    larger EMIs. Borrow within your repayment capacity.
                  </li>
                  <li>
                    <strong>Interest Rate:</strong> Even small variations in
                    interest rates can significantly affect EMI and total cost.
                  </li>
                  <li>
                    <strong>Tenure:</strong> Shorter tenures increase EMI but
                    reduce total interest paid. Longer terms reduce EMI but
                    increase total interest.
                  </li>
                  <li>
                    <strong>Gold Purity & Valuation:</strong> Loan amount is
                    based on purity (karat) and market value of gold.
                  </li>
                  <li>
                    <strong>Prepayment Rules:</strong> SBI allows foreclosing
                    gold loans anytime with minimal penalties, lowering interest
                    cost.
                  </li>
                  <li>
                    <strong>Loan to Value (LTV):</strong> SBI typically allows
                    up to 75% LTV. A lower LTV reduces EMI for the same
                    repayment period.
                  </li>
                  <li>
                    <strong>Existing Gold Loans:</strong> Outstanding gold loan
                    liabilities can affect your eligibility and EMI structure.
                  </li>
                </ol>
              </div>
            </div>
      
          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>

                    {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SbiGoldLoanEmiCalcu;
